// One generation counter for the page's async work. Pure, DOM-free.
//
// Every user navigation (open a dataset, go to a view, Back/Forward) starts a
// new generation. Async work takes a ticket when it starts and checks it after
// each await; a stale ticket means the visitor has moved on, and the work must
// not touch state or the DOM. Two quick clicks then end on the dataset clicked
// last, whatever order the responses arrive in.
//
// Chart redraws are finer-grained: a redraw is also superseded by the next
// redraw within the same navigation (a second toggle before the first /series
// returned), so draw tickets go stale on either.

/** True while the work that took it is still current. */
export type Ticket = () => boolean;

export interface Navigation {
  /** A user navigation: every earlier ticket goes stale. */
  go(): Ticket;
  /** A ticket for the current navigation, for work that belongs to it without
   * being a navigation itself (the combinations index, an overlay's meta). */
  current(): Ticket;
  /** A chart redraw: stale on the next redraw or the next navigation. */
  draw(): Ticket;
}

export function createNavigation(): Navigation {
  let nav = 0, draw = 0;
  const ticket = (n: number): Ticket => () => n === nav;
  return {
    go: () => ticket(++nav),
    current: () => ticket(nav),
    draw: () => {
      const n = nav, d = ++draw;
      return () => n === nav && d === draw;
    },
  };
}
